/**
 * Cross Team Sanity Service
 * Handles cross-team sanity request database operations
 */

import { BaseService } from './BaseService.js'
import { COLLECTIONS } from '../config.js'

export class CrossTeamSanityService extends BaseService {
  constructor() {
    super(COLLECTIONS.CROSS_TEAM_SANITY)
  }

  /**
   * Get the next available cross-team testing ID
   * @returns {Promise<string>} Next ID (e.g., cross_team_testing_00001)
   */
  async getNextCrossTeamTestingId() {
    try {
      // Get all sanity requests
      const allRequests = await this.getAll()
      
      // Extract existing IDs and find the highest number
      const existingIds = allRequests
        .map(request => request.id)
        .filter(id => id && id.startsWith('cross_team_testing_'))
        .map(id => {
          const match = id.match(/^cross_team_testing_(\d+)$/)
          return match ? parseInt(match[1], 10) : 0
        })
        .filter(num => !isNaN(num))
      
      // Find the next available ID
      let nextId = 1
      if (existingIds.length > 0) {
        const maxId = Math.max(...existingIds)
        nextId = maxId + 1
      }
      
      // Format as cross_team_testing_00001, cross_team_testing_00002, etc.
      return `cross_team_testing_${nextId.toString().padStart(5, '0')}`
    } catch (error) {
      console.error('Error getting next cross-team testing ID:', error)
      // Fallback to timestamp-based ID if there's an error
      return `cross_team_testing_${Date.now().toString().slice(-5)}`
    }
  }

  /**
   * Get the next available sanity ID
   * @returns {Promise<string>} Next sanity ID (e.g., cross_team_testing_00001)
   */
  async getNextSanityId() {
    try {
      // Get all sanity requests sorted by creation date
      const allRequests = await this.getAll()
      
      // Extract existing sanity IDs and find the highest number
      const existingIds = allRequests
        .map(request => request.sanity_id)
        .filter(id => id && (id.startsWith('cross_team_testing_') || id.startsWith('SAN')))
        .map(id => {
          // Try new format first
          const newMatch = id.match(/^cross_team_testing_(\d+)$/)
          if (newMatch) {
            return parseInt(newMatch[1], 10)
          }
          // Fallback to legacy SAN format
          const legacyMatch = id.match(/^SAN(\d+)$/)
          return legacyMatch ? parseInt(legacyMatch[1], 10) : 0
        })
        .filter(num => !isNaN(num) && num > 0)
      
      // Find the next available ID
      let nextId = 1
      if (existingIds.length > 0) {
        const maxId = Math.max(...existingIds)
        nextId = maxId + 1
      }
      
      // Format as cross_team_testing_00001, cross_team_testing_00002, etc.
      return `cross_team_testing_${nextId.toString().padStart(5, '0')}`
    } catch (error) {
      // Fallback to timestamp-based ID if there's an error
      return `cross_team_testing_${Date.now().toString().slice(-5)}`
    }
  }

  /**
   * Create a new cross-team sanity request
   * @param {Object} sanityData - Sanity request data
   * @returns {Promise<Object>} Created sanity request
   */
  async createSanityRequest(sanityData) {
    try {
      // Generate unique ID with cross_team_testing prefix
      const id = await this.getNextCrossTeamTestingId()
      
      // Generate sanity_id (e.g., cross_team_testing_00001)
      const sanity_id = await this.getNextSanityId()
      
      // Prepare sanity request data
      const newSanityRequest = {
        ...sanityData,
        id,
        sanity_id,
        status: sanityData.status || 'active',
        teams: sanityData.teams || [],
        activities: sanityData.activities || [],
        is_deleted: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
      
      // Save to database
      const created = await this.create(newSanityRequest)
      return created
    } catch (error) {
      console.error('Error creating sanity request:', error)
      throw error
    }
  }

  /**
   * Update a sanity request
   * @param {string} id - Sanity request ID
   * @param {Object} updateData - Data to update
   * @returns {Promise<Object>} Updated sanity request
   */
  async updateSanityRequest(id, updateData) {
    return await this.update(id, {
      ...updateData,
      updated_at: new Date().toISOString()
    })
  }

  /**
   * Add a team to a sanity request
   * @param {string} sanityId - Sanity request ID
   * @param {Object} teamData - Team data (teamId, teamName, assignedMembers, status)
   * @returns {Promise<Object>} Updated sanity request
   */
  async addTeam(sanityId, teamData) {
    const sanityRequest = await this.getById(sanityId)
    if (!sanityRequest) {
      throw new Error('Sanity request not found')
    }

    const teams = sanityRequest.teams || []
    teams.push({
      ...teamData,
      status: teamData.status || 'yet_to_start'
    })

    return await this.update(sanityId, { 
      teams,
      updated_at: new Date().toISOString()
    })
  }

  /**
   * Update a team in a sanity request
   * @param {string} sanityId - Sanity request ID
   * @param {string} teamId - Team ID
   * @param {Object} teamData - Updated team data
   * @returns {Promise<Object>} Updated sanity request
   */
  async updateTeam(sanityId, teamId, teamData) {
    const sanityRequest = await this.getById(sanityId)
    if (!sanityRequest) {
      throw new Error('Sanity request not found')
    }

    const teams = sanityRequest.teams || []
    const teamIndex = teams.findIndex(t => t.teamId === teamId)
    
    if (teamIndex === -1) {
      throw new Error(`Team ${teamId} not found in sanity request`)
    }

    teams[teamIndex] = {
      ...teams[teamIndex],
      ...teamData
    }

    return await this.update(sanityId, { 
      teams,
      updated_at: new Date().toISOString()
    })
  }

  /**
   * Remove a team from a sanity request
   * @param {string} sanityId - Sanity request ID
   * @param {string} teamId - Team ID
   * @returns {Promise<Object>} Updated sanity request
   */
  async removeTeam(sanityId, teamId) {
    const sanityRequest = await this.getById(sanityId)
    if (!sanityRequest) {
      throw new Error('Sanity request not found')
    }

    const teams = (sanityRequest.teams || []).filter(t => t.teamId !== teamId)

    return await this.update(sanityId, { 
      teams,
      updated_at: new Date().toISOString()
    })
  }

  /**
   * Get active sanity requests
   * @returns {Promise<Array>} Active sanity requests
   */
  async getActiveSanityRequests() {
    const allRequests = await this.getAll()
    return allRequests.filter(request => 
      request.status === 'active' && !request.is_deleted
    )
  }

  /**
   * Soft delete a sanity request
   * @param {string} id - Sanity request ID
   * @param {string} deletedBy - User ID who deleted
   * @returns {Promise<Object>} Updated sanity request
   */
  async softDelete(id, deletedBy) {
    return await this.update(id, {
      is_deleted: true,
      deleted_at: new Date().toISOString(),
      deleted_by: deletedBy,
      updated_at: new Date().toISOString()
    })
  }
}

